import { CarrierDesk } from 'lib/hosts/quoting/carrierdesk';
import { CarrierPoint } from 'lib/hosts/quoting/carrierpoint';
import { E2Open } from 'lib/hosts/quoting/e2open';
import { EmergeMarket } from 'lib/hosts/quoting/emergemarket';
import { FreightView } from 'lib/hosts/quoting/freightview';
import { InsightTMS } from 'lib/hosts/quoting/insight-tms';
import { QuotingPortal, newQuotingPortal } from 'lib/hosts/quoting/interface';
import { Kewill } from 'lib/hosts/quoting/kewill';
import { Manhattan } from 'lib/hosts/quoting/manhattan';
import { NavisphereCarrier } from 'lib/hosts/quoting/navisphere-carrier';
import { OTM } from 'lib/hosts/quoting/otm';
import { PrincetonTmx } from 'lib/hosts/quoting/princeton-tmx';
import { ShipperGuide } from 'lib/hosts/quoting/shipperguide';
import { Shipwell } from 'lib/hosts/quoting/shipwell';
import { Transporeon } from 'lib/hosts/quoting/transporeon';
import { TruckerTools } from 'lib/hosts/quoting/truckertools';
import { UberFreight } from 'lib/hosts/quoting/uberfreight';
import { Undef } from 'types/UtilityTypes';
import { QuotingPortals } from 'types/enums/Integrations';
import { SuggestionSourceCategories } from 'types/suggestions/QuoteSuggestions';

// Channel + page context for Quick Quote payloads.
// Known portals set sourceCategory + source (IntegrationName enum).
// Any other http(s) page still sends sourceURL + sourceHost (no enum).
export interface QuickQuoteSourceContext {
  sourceCategory?: SuggestionSourceCategories;
  source?: QuotingPortals;
  sourceURL?: string;
  sourceHost?: string;
  sourceExternalID?: string;
}

export interface ResolveQuickQuoteSourceContextArgs {
  tabURL?: string;
  hasEmail: boolean;
}

interface ParsedTabPage {
  sourceURL: string;
  sourceHost: string;
}

// Parse http(s) tab URLs only — skip chrome://, extension pages, about:blank, etc.
function parseTabPage(tabURL: Undef<string>): Undef<ParsedTabPage> {
  if (!tabURL) {
    return undefined;
  }

  try {
    const parsed = new URL(tabURL);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return undefined;
    }

    const host = parsed.hostname.replace(/^www\./i, '');
    if (!host) {
      return undefined;
    }

    const page: ParsedTabPage = {
      sourceURL: tabURL,
      sourceHost: host,
    };

    return page;
  } catch {
    return undefined;
  }
}

function quotingPortalSource(portal: QuotingPortal): Undef<QuotingPortals> {
  if (portal instanceof FreightView) {
    return QuotingPortals.FreightView;
  }
  if (portal instanceof E2Open) {
    return QuotingPortals.E2Open;
  }
  if (portal instanceof UberFreight) {
    return QuotingPortals.UberFreight;
  }
  if (portal instanceof Shipwell) {
    return QuotingPortals.Shipwell;
  }
  if (portal instanceof NavisphereCarrier) {
    return QuotingPortals.NavisphereCarrier;
  }
  if (portal instanceof CarrierPoint) {
    return QuotingPortals.CarrierPoint;
  }
  if (portal instanceof OTM) {
    return QuotingPortals.OTM;
  }
  if (portal instanceof CarrierDesk) {
    return QuotingPortals.CarrierDesk;
  }
  if (portal instanceof ShipperGuide) {
    return QuotingPortals.ShipperGuide;
  }
  if (portal instanceof Transporeon) {
    return QuotingPortals.Transporeon;
  }
  if (portal instanceof EmergeMarket) {
    return QuotingPortals.EmergeMarket;
  }
  if (portal instanceof TruckerTools) {
    return QuotingPortals.TruckerTools;
  }
  if (portal instanceof Manhattan) {
    return QuotingPortals.Manhattan;
  }
  if (portal instanceof InsightTMS) {
    return QuotingPortals.InsightTMS;
  }
  if (portal instanceof PrincetonTmx) {
    return QuotingPortals.PrincetonTmx;
  }
  if (portal instanceof Kewill) {
    return QuotingPortals.Kewill;
  }
  return undefined;
}

// Resolve page/email source for a Quick Quote request from the bound tab URL and
// whether an email is in context. Reuses newQuotingPortal for known portals.
// Any other http(s) page still captures sourceHost + sourceURL (Sophie's standup scope).
export function resolveQuickQuoteSourceContext(
  args: ResolveQuickQuoteSourceContextArgs
): Undef<QuickQuoteSourceContext> {
  const page = parseTabPage(args.tabURL);
  const portal = page ? newQuotingPortal(args.tabURL) : undefined;
  const portalSource = portal ? quotingPortalSource(portal) : undefined;

  if (page && portalSource) {
    const portalContext: QuickQuoteSourceContext = {
      sourceCategory: SuggestionSourceCategories.QuotingPortal,
      source: portalSource,
      sourceURL: page.sourceURL,
      sourceHost: page.sourceHost,
    };

    return portalContext;
  }

  if (page) {
    const pageContext: QuickQuoteSourceContext = {
      ...(args.hasEmail && {
        sourceCategory: SuggestionSourceCategories.Email,
      }),
      sourceURL: page.sourceURL,
      sourceHost: page.sourceHost,
    };

    return pageContext;
  }

  if (args.hasEmail) {
    const emailContext: QuickQuoteSourceContext = {
      sourceCategory: SuggestionSourceCategories.Email,
    };

    return emailContext;
  }

  return undefined;
}
